import { useContext, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { fetchListByName } from 'reporting-data/apis/lists';
import { isConfigReport, isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
// @ts-expect-error Untyped dependency
import { get as isUnified } from 'reporting-data/retrieve/unified/supported';
import { errorAlert, successAlert } from './util';
import { LIST_EXPORT_TYPES } from '../../types/exports';
import AddToList from './AddToList';
import CreateNewList from './CreateNewList';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getDataType } from 'reporting-data/report/reportGetters';
import { getOrConvertToObjectTypeId } from 'reporting-data/crmObjects/tsUtils';
import { Small } from 'foundations-components/transitional/components/Small';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getObjectTypeIdForList = report => {
  if (isJourneyReport(report)) {
    return report.getIn(['journeyQuery', 'objectQuery', 'table', 'objectTypeId'], null);
  } else if (isUnified(getDataType(report))) {
    return '0-1';
  } else if (isConfigReport(report)) {
    return getOrConvertToObjectTypeId(report.get('config'));
  }
  return null;
};
export function ExportToListModal({
  setModalOpen,
  onSubmit,
  report,
  extraInfo
}) {
  const [createMethod, setCreateMethod] = useState(LIST_EXPORT_TYPES.NEW);
  const [nameValidationError, setNameValidationError] = useState(false);
  const [createListName, setCreateListName] = useState('');
  const [listToAdd, setListToAdd] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const {
    gates
  } = useContext(UserInfoContext);
  useEffect(() => {
    if (createMethod === LIST_EXPORT_TYPES.NEW && createListName === '') {
      setNameValidationError(false);
    }
  }, [createListName, createMethod]);
  const objectTypeId = getObjectTypeIdForList(report);
  if (!objectTypeId) {
    FloatingAlertStore.addAlert(errorAlert(createMethod));
    setModalOpen(false);
    return null;
  }
  const onListNameChange = ({
    target: {
      value
    }
  }) => {
    setCreateListName(value);
    setNameValidationError(false);
  };
  const handleSubmitClick = listNameOrId => {
    setIsLoading(true);
    onSubmit(createMethod, report, listNameOrId, gates).then(() => {
      FloatingAlertStore.addAlert(successAlert(createMethod, listNameOrId));
      setModalOpen(false);
    }).catch(() => {
      setIsLoading(false);
      FloatingAlertStore.addAlert(errorAlert(createMethod));
    });
  };
  const onSubmitClick = () => {
    if (createMethod === LIST_EXPORT_TYPES.ADD && listToAdd) {
      handleSubmitClick(listToAdd);
    }
    if (createMethod === LIST_EXPORT_TYPES.NEW) {
      fetchListByName(createListName, objectTypeId).then(() => {
        setNameValidationError(true);
      }).catch(e => {
        if (e.status === 404) {
          handleSubmitClick(createListName);
        }
      });
    }
  };
  const onListChange = ({
    target: {
      value
    }
  }) => {
    if (value) {
      setListToAdd(value);
    } else {
      setListToAdd(undefined);
    }
  };
  const disabled = createMethod === LIST_EXPORT_TYPES.NEW && createListName === '' || createMethod === LIST_EXPORT_TYPES.ADD && !listToAdd;
  return /*#__PURE__*/_jsxs(Modal, {
    width: 575,
    children: [isLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
      layout: "centered"
    }), /*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => setModalOpen(false)
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.export.listModal.header"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(Form, {
        children: /*#__PURE__*/_jsxs(ToggleGroup, {
          name: "list-export-radios",
          children: [/*#__PURE__*/_jsx(RadioInput, {
            name: "createNewList",
            size: "small",
            checked: createMethod === LIST_EXPORT_TYPES.NEW,
            onChange: () => setCreateMethod(LIST_EXPORT_TYPES.NEW),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-ui-components.export.listModal.radios.new"
            })
          }), createMethod === LIST_EXPORT_TYPES.NEW && /*#__PURE__*/_jsx(CreateNewList, {
            nameValidationError: nameValidationError,
            createListName: createListName,
            onListNameChange: onListNameChange
          }), /*#__PURE__*/_jsx(RadioInput, {
            name: "addToList",
            size: "small",
            checked: createMethod === LIST_EXPORT_TYPES.ADD,
            onChange: () => setCreateMethod(LIST_EXPORT_TYPES.ADD),
            marginBlock: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-ui-components.export.listModal.radios.add"
            })
          }), objectTypeId && createMethod === LIST_EXPORT_TYPES.ADD && /*#__PURE__*/_jsx(AddToList, {
            listToAdd: listToAdd,
            objectTypeId: objectTypeId,
            onListChange: onListChange
          })]
        })
      }), extraInfo ? /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: extraInfo
      }) : null]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        type: "submit",
        disabled: disabled,
        onClick: onSubmitClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.export.listModal.submit"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: () => setModalOpen(false),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.export.listModal.cancel"
        })
      })]
    })]
  });
}